import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { EU_COUNTRIES, localizeCountryName } from "@/constants/countries";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_destinations-preview/artifacts/s9uss3m3_LOGO%20UFFICIALE%20TDE%20%281%29.png";

// EU 27 countries as dropdown items — built from the shared constants list.
// Labels are localized at render time via localizeCountryName().
const EU27_ITEMS = EU_COUNTRIES.map((c) => ({
  label: c.name,
  label_it: c.name_it,
  href: `/country/${c.slug}`,
  code: c.code,
}));

// Each item references an i18n key under "nav.*" for its label + sub-labels.
const NAV = [
  {
    key: "about",
    labelKey: "nav.about",
    testId: LANDING.navHome,
    items: [
      { labelKey: "nav.aboutProject", href: "/about" },
      { labelKey: "nav.ourTeam", href: "/our-team" },
      { labelKey: "nav.internationalBoard", href: "/international-board" },
      { labelKey: "nav.nationalProjectTeam", href: "/national-project-team" },
      { labelKey: "nav.partners", href: "/partners" },
      { labelKey: "nav.newsArchive", href: "/news-archive" },
    ],
  },
  {
    key: "activities",
    labelKey: "nav.activities",
    testId: LANDING.navDestinazioni,
    items: [
      { labelKey: "nav.overview", href: "/activities" },
      { labelKey: "nav.training", href: "/activities/formazione" },
      { labelKey: "nav.publishing", href: "/activities/editoria" },
      { labelKey: "nav.networking", href: "/activities/networking" },
      {
        labelKey: "nav.eventsBig",
        href: "/activities/eventi",
        children: [
          { labelKey: "nav.inBetween", href: "/articles/in-between-2026-05-04" },
        ],
      },
      { labelKey: "nav.mission", href: "/mission" },
    ],
  },
  {
    key: "treasures",
    labelKey: "nav.treasures2026",
    testId: LANDING.navEsperienze,
    href: "/treasures-2026",
  },
  {
    key: "eu-today",
    labelKey: "nav.euToday",
    testId: LANDING.navTesori,
    items: [
      { labelKey: "nav.overview", href: "/eu-today" },
      { labelKey: "nav.euHeritage", href: "/eu-heritage" },
      { labelKey: "nav.euCEE", href: "/eu-cee" },
      { labelKey: "nav.roadTo27", href: "/road-to-27" },
      { labelKey: "nav.euEuro", href: "/eu-euro" },
      { labelKey: "nav.euLanguages", href: "/eu-languages" },
    ],
  },
  {
    key: "eu27",
    labelKey: "nav.eu27",
    testId: LANDING.navBlog,
    items: EU27_ITEMS,
    isMega: true,
  },
  {
    key: "contact",
    labelKey: "nav.contact",
    testId: LANDING.navContatti,
    href: "/contact",
  },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileSubOpen, setMobileSubOpen] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setMobileSubOpen(null);
  }, [location.pathname, location.hash]);

  const handleEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  return (
    <header
      data-testid={LANDING.header}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#e2e8f0]/70 py-2"
          : "bg-white/85 backdrop-blur-sm py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link
          to="/"
          data-testid={LANDING.headerLogo}
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src={LOGO_URL}
            alt="Tesori d'Europa"
            style={{ mixBlendMode: "multiply" }}
            className={`w-auto transition-all duration-500 ${
              scrolled ? "h-10 md:h-12" : "h-12 md:h-14"
            }`}
          />
        </Link>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.items ? (
              <div
                key={item.key}
                className="relative"
                onMouseEnter={() => handleEnter(item.key)}
                onMouseLeave={handleLeave}
              >
                <button
                  data-testid={`nav-${item.key}`}
                  className="px-3 py-2 inline-flex items-center gap-1.5 font-body text-[12px] tracking-[0.18em] uppercase text-[#1e293b] hover:text-[#d4af37] transition-colors"
                >
                  {t(item.labelKey)}
                  <ChevronDown
                    size={12}
                    strokeWidth={2}
                    className={`transition-transform ${
                      openMenu === item.key ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* Dropdown */}
                <div
                  className={`absolute top-full pt-3 transition-all duration-200 ${
                    item.isMega
                      ? "right-0 lg:right-auto lg:left-1/2 lg:-translate-x-1/2 w-[560px] max-w-[92vw]"
                      : "left-1/2 -translate-x-1/2 min-w-[260px]"
                  } ${
                    openMenu === item.key
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-1"
                  }`}
                >
                  <div className="bg-white border border-[#e2e8f0] shadow-[0_20px_50px_-20px_rgba(15,36,64,0.25)]">
                    {item.isMega ? (
                      <div className="p-3 grid grid-cols-3 gap-x-2 gap-y-0.5 max-h-[70vh] overflow-y-auto">
                        {item.items.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            data-testid={`nav-eu27-${sub.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                            className="flex items-center gap-2.5 px-3 py-2 font-body text-[12.5px] text-[#1a365d] hover:bg-[#fafaf7] hover:text-[#d4af37] transition-colors"
                          >
                            <img
                              src={`https://flagcdn.com/24x18/${sub.code}.png`}
                              srcSet={`https://flagcdn.com/48x36/${sub.code}.png 2x`}
                              width="22"
                              height="16"
                              alt=""
                              className="border border-[#e2e8f0] shrink-0"
                              loading="lazy"
                            />
                            <span className="truncate">{localizeCountryName({ name: sub.label, name_it: sub.label_it }, i18n.language)}</span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="px-1.5 py-2.5">
                        {item.items.map((sub) =>
                          sub.children ? (
                            <div key={sub.href} className="relative group/sub">
                              <Link
                                to={sub.href}
                                data-testid={`nav-${item.key}-${sub.href.replace(/[^a-z0-9]/gi, "-")}`}
                                className="flex items-center justify-between px-4 py-2.5 font-body text-[13px] text-[#1a365d] hover:bg-[#fafaf7] hover:text-[#d4af37] transition-colors"
                              >
                                {t(sub.labelKey)}
                                <ChevronDown
                                  size={12}
                                  strokeWidth={2}
                                  className="-rotate-90"
                                />
                              </Link>
                              {/* Flyout for sub-sub-menu (3rd level) */}
                              <div className="invisible opacity-0 group-hover/sub:visible group-hover/sub:opacity-100 transition-all absolute left-full top-0 ml-1 min-w-[240px] bg-white border border-[#e2e8f0] shadow-[0_20px_50px_-20px_rgba(15,36,64,0.25)]">
                                <div className="px-1.5 py-2.5">
                                  {sub.children.map((leaf) => (
                                    <Link
                                      key={leaf.href}
                                      to={leaf.href}
                                      data-testid={`nav-${item.key}-${sub.labelKey.split(".").pop()}-${leaf.labelKey.split(".").pop()}`}
                                      className="block px-4 py-2.5 font-body text-[13px] text-[#1a365d] hover:bg-[#fafaf7] hover:text-[#d4af37] transition-colors"
                                    >
                                      {t(leaf.labelKey)}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <Link
                              key={sub.href}
                              to={sub.href}
                              data-testid={`nav-${item.key}-${sub.href.replace(/[^a-z0-9]/gi, "-")}`}
                              className="block px-4 py-2.5 font-body text-[13px] text-[#1a365d] hover:bg-[#fafaf7] hover:text-[#d4af37] transition-colors"
                            >
                              {t(sub.labelKey)}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.key}
                to={item.href}
                data-testid={`nav-${item.key}`}
                className="px-3 py-2 font-body text-[12px] tracking-[0.18em] uppercase text-[#1e293b] hover:text-[#d4af37] transition-colors"
              >
                {t(item.labelKey)}
              </Link>
            )
          )}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 shrink-0">
          <LanguageSwitcher variant="desktop" />
          <button
            data-testid={LANDING.navSearch}
            aria-label={t("nav.search")}
            className="hidden lg:inline-flex h-10 w-10 items-center justify-center text-[#1a365d] hover:text-[#d4af37] transition-colors"
          >
            <Search size={18} strokeWidth={1.6} />
          </button>
          <Link
            to="/contact"
            data-testid={LANDING.navCta}
            className="btn-primary hidden lg:inline-flex"
          >
            {t("nav.cta")}
          </Link>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid={LANDING.mobileMenuToggle}
            aria-label={t("nav.openMenu")}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center text-[#1a365d]"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid={LANDING.mobileMenu}
          className="lg:hidden bg-white border-t border-[#e2e8f0] max-h-[80vh] overflow-y-auto"
        >
          <div className="px-6 py-4 flex flex-col">
            {NAV.map((item) =>
              item.items ? (
                <div key={item.key} className="border-b border-[#e2e8f0]/60">
                  <button
                    onClick={() =>
                      setMobileSubOpen(mobileSubOpen === item.key ? null : item.key)
                    }
                    className="w-full flex items-center justify-between py-4 font-body text-sm tracking-[0.18em] uppercase text-[#1e293b]"
                  >
                    {t(item.labelKey)}
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${
                        mobileSubOpen === item.key ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {mobileSubOpen === item.key && (
                    <div className={`pb-3 pl-2 ${item.isMega ? "grid grid-cols-2 gap-x-2 max-h-[60vh] overflow-y-auto" : "flex flex-col gap-1"}`}>
                      {item.items.map((sub) => (
                        <div key={sub.href} className="flex flex-col">
                          <Link
                            to={sub.href}
                            data-testid={item.isMega ? `mobile-nav-eu27-${sub.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}` : undefined}
                            className={`py-2 font-body text-[13px] text-[#1a365d] ${item.isMega ? "flex items-center gap-2" : ""}`}
                          >
                            {item.isMega && sub.code && (
                              <img
                                src={`https://flagcdn.com/24x18/${sub.code}.png`}
                                width="20"
                                height="14"
                                alt=""
                                className="border border-[#e2e8f0] shrink-0"
                                loading="lazy"
                              />
                            )}
                            <span className="truncate">{item.isMega ? localizeCountryName({ name: sub.label, name_it: sub.label_it }, i18n.language) : t(sub.labelKey)}</span>
                          </Link>
                          {sub.children && (
                            <div className="pl-4 border-l border-[#d4af37]/40 ml-2 flex flex-col">
                              {sub.children.map((leaf) => (
                                <Link
                                  key={leaf.href}
                                  to={leaf.href}
                                  className="py-1.5 font-body text-[12.5px] text-[#1a365d]/85 hover:text-[#d4af37]"
                                >
                                  {t(leaf.labelKey)}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.key}
                  to={item.href}
                  className="py-4 font-body text-sm tracking-[0.18em] uppercase text-[#1e293b] border-b border-[#e2e8f0]/60"
                >
                  {t(item.labelKey)}
                </Link>
              )
            )}
            <Link to="/contact" className="btn-primary justify-center mt-5">
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
